import { useTranslation } from "react-i18next";
import { MapPin } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export type DeliveryAddressSummaryCardProps = {
  /** Main line(s) — full address text */
  formattedAddress: string;
  label?: string | null;
  isDefault?: boolean;
  contactPhone?: string | null;
  contactPersonName?: string | null;
  courierNotes?: string | null;
  /** Match selected saved-address card styling */
  emphasized?: boolean;
  className?: string;
};

/**
 * Read-only delivery summary using the same visual language as saved address cards in AddressPicker.
 */
export function DeliveryAddressSummaryCard({
  formattedAddress,
  label,
  isDefault = false,
  contactPhone,
  contactPersonName,
  courierNotes,
  emphasized = true,
  className,
}: DeliveryAddressSummaryCardProps): JSX.Element {
  const { t } = useTranslation();
  const mainAddress = formattedAddress.trim() || t("addressPicker.noFormatted");
  const labelTrim = label?.trim() ?? "";

  return (
    <Card
      className={cn(
        "border transition-colors duration-150 motion-reduce:transition-none",
        emphasized
          ? "border-primary bg-primary/5 ring-1 ring-primary/25"
          : "border-border",
        className,
      )}
    >
      <CardContent className="flex flex-col gap-3 p-3 sm:flex-row sm:items-start">
        <div className="flex min-w-0 flex-1 gap-3">
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary motion-reduce:transition-none"
            aria-hidden
          >
            <MapPin className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            {labelTrim ? (
              <>
                <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {labelTrim}
                </span>
                <span className="mt-1 block text-sm font-medium leading-snug text-foreground">
                  {mainAddress}
                </span>
              </>
            ) : (
              <span className="block text-sm font-medium leading-snug text-foreground">{mainAddress}</span>
            )}
            {isDefault ? (
              <Badge variant="secondary" className="mt-1.5 text-xs font-normal">
                {t("addressPicker.defaultBadge")}
              </Badge>
            ) : null}
            {contactPhone?.trim() ? (
              <p className="mt-1.5 text-xs text-muted-foreground">
                {t("addressPicker.savedCardPhone", { phone: contactPhone.trim() })}
              </p>
            ) : null}
            {contactPersonName?.trim() ? (
              <p className="text-xs text-muted-foreground">
                {t("addressPicker.savedCardPerson", { name: contactPersonName.trim() })}
              </p>
            ) : null}
            {courierNotes?.trim() ? (
              <p className="mt-1 line-clamp-3 text-xs text-muted-foreground">
                {t("addressPicker.savedCardCourierNotes", { notes: courierNotes.trim() })}
              </p>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
